<template>
  <div class="qualifications-page">
    <inner-hero
      title="资质证书"
      parent-label="企业概况"
      background-image="/images/company/campus.webp"
      description="按类别、适用主体和有效状态组织可验证资料，缺少正式信息时明确标记审核中。"
    />

    <section
      ref="certificateSection"
      class="section qualifications-section"
      aria-label="资质认证列表"
      :data-route-scroll-path="route.path"
      :data-route-scroll-navigation-id="routeScrollInstanceId"
      :data-route-scroll-ready="isRouteScrollReady ? 'true' : 'false'"
    >
      <div class="page-container">
        <certificate-gallery
          :items="certificates"
          :page="page"
          :page-count="pageCount"
          :pending="pending"
          :error="Boolean(error)"
          :preview-item="previewCertificate"
          @close-preview="closePreview"
          @retry="refresh"
          @update:page="handlePageChange"
        />
      </div>
    </section>

    <contact-action />
  </div>
</template>

<script setup lang="ts">
  import type { ApiData, CertificateItem, PageResponse } from '~/types/site-api'

  import { getRouteScrollNavigationId } from '~/utils/route-scroll'
  import {
    createBreadcrumbJsonLd,
    createSiteUrl,
    getPageSeo,
    getSiteUrl,
    siteSeo,
  } from '~/utils/seo'

  definePageMeta({ layout: 'default' })

  const page = shallowRef(1)
  const routeScrollInstanceId = getRouteScrollNavigationId()
  const isPageMounted = shallowRef(false)
  const certificateScrollRequest = shallowRef(0)
  const loadedPage = shallowRef(0)
  let certificateScrollSequence = 0
  const route = useRoute()
  const certificateSection = useTemplateRef<HTMLElement>('certificateSection')
  const { getCertificate, getCertificates } = useSiteApi()
  const previewId = computed(() => {
    const value = route.query.certificate
    return Array.isArray(value) ? (value[0] ?? '') : (value ?? '')
  })

  const {
    data: certificateResponse,
    pending,
    error,
    status: certificateStatus,
    refresh,
  } = await useAsyncData<PageResponse<CertificateItem>>(
    'site-certificates',
    async (_nuxtApp, { signal }) => {
      const requestedPage = page.value
      loadedPage.value = 0
      try {
        const response = await getCertificates({ page: requestedPage, page_limit: 8 }, signal)
        if (!signal.aborted) loadedPage.value = requestedPage
        return response
      } catch (requestError) {
        if (!signal.aborted) loadedPage.value = requestedPage
        throw requestError
      }
    },
    { watch: [page] }
  )

  if (
    import.meta.client &&
    certificateResponse.value !== undefined &&
    (certificateStatus.value === 'success' || certificateStatus.value === 'error')
  ) {
    loadedPage.value = page.value
  }

  const certificates = computed(() => certificateResponse.value?.data ?? [])
  const pageCount = computed(() => certificateResponse.value?.meta.last_page ?? 1)
  const isRouteScrollReady = computed(
    () =>
      !pending.value &&
      loadedPage.value === page.value &&
      (certificateStatus.value === 'success' || certificateStatus.value === 'error')
  )

  /** 将证书列表起点带回视口。 */
  function scrollToCertificateSection() {
    if (!import.meta.client) return
    certificateSection.value?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    })
  }

  watch(
    [pending, certificates],
    async ([isPending], _oldValue, onCleanup) => {
      const requestId = certificateScrollRequest.value
      if (!requestId || isPending) return

      let cancelled = false
      onCleanup(() => {
        cancelled = true
      })

      await nextTick()
      if (
        cancelled ||
        requestId !== certificateScrollRequest.value ||
        pending.value ||
        !isPageMounted.value
      )
        return

      certificateScrollRequest.value = 0
      scrollToCertificateSection()
    },
    { flush: 'post' }
  )

  const { data: previewResponse } = await useAsyncData<ApiData<CertificateItem> | null>(
    'site-certificate-preview',
    () => (previewId.value ? getCertificate(previewId.value) : Promise.resolve(null)),
    { watch: [previewId] }
  )

  const previewCertificate = computed(() => previewResponse.value?.data)

  /** 切换接口分页并将列表起点带回视口。 */
  function handlePageChange(nextPage: number) {
    if (isPageMounted.value) certificateScrollRequest.value = ++certificateScrollSequence
    page.value = nextPage
  }

  /** 清除搜索直达预览使用的证书查询参数。 */
  function closePreview() {
    if (!previewId.value) return
    const query = { ...route.query }
    delete query.certificate
    void navigateTo({ path: route.path, query }, { replace: true })
  }

  const seo = getPageSeo('company-qualifications')
  const siteUrl = getSiteUrl()

  useSeoMeta({
    title: seo.title,
    description: seo.description,
    ogTitle: seo.title,
    ogDescription: seo.description,
    ogType: 'website',
    ogImage: siteSeo.ogImage,
    twitterTitle: seo.title,
    twitterDescription: seo.description,
    twitterImage: siteSeo.ogImage,
  })

  useHead({
    meta: [{ name: 'keywords', content: seo.keywords?.join(', ') }],
    link: [{ rel: 'canonical', href: createSiteUrl('/company/qualifications', siteUrl) }],
  })
  useJsonLd(
    createBreadcrumbJsonLd(
      [
        { name: '首页', path: '/' },
        { name: '资质证书', path: '/company/qualifications' },
      ],
      siteUrl
    )
  )

  onMounted(() => {
    isPageMounted.value = true
  })

  onUnmounted(() => {
    isPageMounted.value = false
  })
</script>

<style scoped lang="scss">
  .qualifications-page {
    min-width: 0;
    overflow: hidden;
    text-align: left;
  }

  .qualifications-section {
    scroll-margin-top: 92px;
    background: #f5f6f7;
  }

  @media (max-width: 767px) {
    .qualifications-section {
      scroll-margin-top: 72px;
    }
  }
</style>
